
import Vue from "vue";
import { Component, Prop, Watch } from "vue-property-decorator";
import Toasted from 'vue-toasted';
import { formatDateTime, formatDurationSeconds, formatTime, WindowType } from "../../../util";
import { Board, Card, CardDuration, KanbanTimeTrackingStopRequest, Ticket, TimeTracker } from "../../backend/backendapi";
import { KanbanState } from "../kanban.model";
import Visible from "../../../directives/visible";
import RunningTimeTracker from "./RunningTimeTracker";
import { getCardById, getDurationSecondsSum, getStartTime } from "./util";
import CenterOnParent from "../../util/CenterOnParent";

Vue.use(Toasted, { duration: 2000 });

declare var window: WindowType;

@Component({
  name: 'SelectTimeTracking',
  components: {
    CenterOnParent,
    RunningTimeTracker,
  },
  directives: {
    Visible
  }
})
export default class SelectTimeTracking extends Vue {
  @Prop({ required: true })
  readonly state!: KanbanState;
  @Prop({ required: true })
  readonly selectedBoard!: Board | null;
  @Prop({ required: true })
  readonly selectedFlow!: Ticket | null;
  @Prop({ required: true })
  readonly selectedCard!: Card | null;

  comment: string = '';

  //getter
  get boards(): Board[] {
    return this.state.boards.filter(b => b.tickets?.some(t => t.cards?.some(c => c.responsibles?.some(r => r.userId === this.userId))));
  }

  get flows(): Ticket[] {
    return this.selectedBoard?.tickets?.filter(t => t.cards?.some(c => c.responsibles?.some(r => r.userId === this.userId))) ?? [];
  }

  get cards(): Card[] {
    return this.selectedFlow?.cards?.filter(c => c.responsibles?.some(r => r.userId === this.userId)) ?? [];
  }

  get lastDurationComment(): string {
    if (!this.lastDurationEntry)
      return '';

    return this.lastDurationEntry.comment ?? '';
  }

  get lastDurationEntry(): CardDuration | null {
    let durations = this.selectedCard?.durations?.filter(e => e.userId === this.userId) ?? [];
    if (!durations.length)
      return null;

    return durations[durations?.length - 1];
  }

  get lastDurationStartTime(): string {
    if (!this.lastDurationEntry)
      return '';

    if (Date.now() - this.lastDurationEntry.timeStmp!.getTime() < 24 * 60 * 60 * 1000) {
      return formatTime(this.lastDurationEntry.timeStmp!);
    } else {
      return formatDateTime(this.lastDurationEntry.timeStmp!);
    }
  }

  get startTime(): string {
    if (!this.selectedCard) {
      return '';
    }
    return getStartTime(this.selectedCard);
  }

  get totalDuration(): string {
    if (!this.selectedCard) {
      return '';
    }
    return formatDurationSeconds(getDurationSecondsSum(this.selectedCard));
  }

  get lastDurationTimeSpan(): string {
    if (!this.lastDurationEntry)
      return '';

    return formatDurationSeconds(this.lastDurationEntry.seconds);
  }

  get userId(): string | undefined {
    return window.application.kanban.user?.id;
  }

  //functions
  async start(): Promise<void> {
    this.$emit('start', this.comment);
  }

  async stop(tracker: TimeTracker, comment: string): Promise<void> {
    if (!tracker) {
      return;
    }

    const card = getCardById(this.state, tracker.cardId);
    if (!card) {
      return;
    }

    const duration = await this.state.timeTrackingApi.stop(new KanbanTimeTrackingStopRequest({ id: tracker.id, comment: comment }));
    card.durations ??= [];
    card.durations.push(duration);

    const index = this.state.timeTrackers.findIndex(e => e === tracker);
    if (index > -1) {
      this.state.timeTrackers.splice(index, 1);
    }

    this.$toasted.info(this.$t('txtSaved').toString());
  }

  //watches
  @Watch('selectedCard')
  @Watch('selectedCard.durations', { immediate: true }) //immediate required, otherwise the function is not called
  private onSelectedCardChanged(): void {
    this.comment = this.lastDurationComment;
  }
}
